interface MeasurementCache {
    [key: string]: number;
}

const widthCache: MeasurementCache = {};
const heightCache: MeasurementCache = {};
const charDimensionsCache: Map<string, { width: number; height: number }> = new Map();

let canvasContext: CanvasRenderingContext2D | null = null;

function getCanvasContext(): CanvasRenderingContext2D {
    if (!canvasContext) {
        const canvas = document.createElement('canvas');
        canvasContext = canvas.getContext('2d');
        if (!canvasContext) {
            throw new Error('Failed to get canvas context for text measurement');
        }
    }
    return canvasContext;
}

export function measureTextWidth(text: string, fontFamily: string, fontSize: number): number {
    const cacheKey = `${text}|${fontFamily}|${fontSize}`;

    if (widthCache[cacheKey] !== undefined) {
        return widthCache[cacheKey];
    }

    const ctx = getCanvasContext();
    ctx.font = `${fontSize}px "${fontFamily}", sans-serif`;
    const width = ctx.measureText(text).width;

    widthCache[cacheKey] = width;
    return width;
}

export function measureTextHeight(text: string, fontFamily: string, fontSize: number, lineHeightRatio: number = 1.15): number {
    const cacheKey = `${text}|${fontFamily}|${fontSize}|${lineHeightRatio}`;

    if (heightCache[cacheKey] !== undefined) {
        return heightCache[cacheKey];
    }

    const effectiveLines = text.split('\n').filter(line => line.trim());

    if (effectiveLines.length === 0) {
        heightCache[cacheKey] = fontSize;
        return fontSize;
    }

    const contentHeight = measureContentHeight(text, fontFamily, fontSize, lineHeightRatio);
    const height = contentHeight + fontSize * (lineHeightRatio - 1);

    heightCache[cacheKey] = height;
    return height;
}


export function measureContentHeight(text: string, fontFamily: string, fontSize: number, lineHeightRatio: number = 1.15): number {
    const cacheKey = `content|${text}|${fontFamily}|${fontSize}|${lineHeightRatio}`;

    if (heightCache[cacheKey] !== undefined) {
        return heightCache[cacheKey];
    }

    const lines = text.split('\n');
    const effectiveLines = lines.filter(line => line.trim());

    if (effectiveLines.length === 0) {
        heightCache[cacheKey] = fontSize;
        return fontSize;
    }

    if (effectiveLines.length === 1) {
        heightCache[cacheKey] = fontSize;
        return fontSize;
    }

    const lineHeight = fontSize * lineHeightRatio;
    const totalHeight = (lines.length - 1) * lineHeight + fontSize;

    heightCache[cacheKey] = totalHeight;
    return totalHeight;
}

export function measureContentDimensions(
    text: string,
    fontFamily: string,
    fontSize: number,
    lineHeightRatio: number = 1.15
): { width: number; height: number } {
    const lines = text.split('\n').filter(line => line.trim());

    if (lines.length === 0) {
        return { width: 0, height: measureContentHeight(text, fontFamily, fontSize, lineHeightRatio) };
    }

    let maxWidth = 0;
    for (const line of lines) {
        const lineWidth = measureTextWidth(line, fontFamily, fontSize);
        maxWidth = Math.max(maxWidth, lineWidth);
    }

    const totalHeight = measureContentHeight(text, fontFamily, fontSize, lineHeightRatio);

    return { width: maxWidth, height: totalHeight };
}

export function calculateCharDimensions(
    fontFamily: string,
    fontSize: number,
    lineHeightRatio: number = 1.15
): { width: number; height: number } {
    const cacheKey = `${fontFamily}|${fontSize}|${lineHeightRatio}`;

    if (charDimensionsCache.has(cacheKey)) {
        return charDimensionsCache.get(cacheKey)!;
    }

    const charWidth = measureTextWidth('0', fontFamily, fontSize);
    const charHeight = measureTextHeight('0', fontFamily, fontSize, lineHeightRatio);

    const dimensions = { width: charWidth, height: charHeight };
    charDimensionsCache.set(cacheKey, dimensions);

    return dimensions;
}

export function clearMeasurementCache(): void {
    Object.keys(widthCache).forEach(key => delete widthCache[key]);
    Object.keys(heightCache).forEach(key => delete heightCache[key]);
    charDimensionsCache.clear();
}
